import React, { useEffect, useState } from "react";
import { getCashPrice } from "api/actions/VisitActions";
import BottomFixedButton from "components/bottomFixedButton/BottomFixedButton";
import ChoosePaymentMethod from "components/choosePaymentMethod/ChoosePaymentMethod";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import { useApiQuery } from "hooks/useApiQuery";
import { useCurrentUser } from "hooks/useCurrentUser";
import usePatients from "hooks/visit/usePatients";
import { useDermatologyVisitFlow } from "hooks/useVisitFlow";
import { dermatology_sub_steps, speciality } from "helpers/enum";
import useModal from "hooks/useModal";
import InsuranceUpdateModal from "../../common/InsuranceUpdateModal";
import { dermaVisitFlowReducers } from "reducers/dermatology/dermaVisitFlowReducer";
import { useDispatch } from "react-redux";
import LoaderComponent from "components/loader/LoaderComponent";
import useUpdateDermatologyVisit from "hooks/visit/useUpdateDermatologyVisit";
import { useHistory } from "react-router-dom";
import useVisitStepChange from "hooks/useVisitStepChange";
// Network check removed per new requirement — do not call check-insurance API.
import { trackDropOffRate, trackEvent, trackPageView } from "helpers/analytics";
import HelpButton from "../../common/HelpButton";
import Button from "components/buttons/Button";
import SimpleHeader from "components/header/SimpleHeader";
import classNames from "classnames";
import useToastify from "hooks/useToastify";
import { excludeImageContainer } from "helpers/validationSchema";

function DermatologyVisitPricing() {
  const [selected, setSelected] = useState(null);
  const [insurance_details, setInsuranceDetails] = useState(null);

  const { showAlert } = useToastify();
  const dispatch = useDispatch();
  const history = useHistory();
  const { setShowModal } = useModal();
  const { userDetails } = useCurrentUser();
  const { loading } = useVisitStepChange();
  const { incompleteData, paymentMethod } = useDermatologyVisitFlow();
  const {
    getPatients,
    patientsList,
    fetchingPatients,
    getCurrentPatient,
    getAccountHolder,
  } = usePatients();
  const currentPatient = getCurrentPatient(
    incompleteData?.patient_id,
    patientsList
  );
  const { updateVisit } = useUpdateDermatologyVisit();
  const {
    payload: cashPriceResponse,
    loading: fetchingCashPrice,
    query: fetchCashPrice,
  } = useApiQuery(
    getCashPrice(incompleteData?.patient_id, speciality.dermatology),
    false
  );

  function hasInsurance(patient) {
    if (patient?.insurance && patient?.insurance?.insurance_company) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
    if (paymentMethod) {
      setSelected(paymentMethod);
    }
  }, [paymentMethod, setSelected]);

  useEffect(() => {
    if (insurance_details?.member_id) {
      // Allow proceeding with insurance even if previously marked out_of_network.
      setSelected("insurance");
    }
  }, [insurance_details]);

  useEffect(() => {
    if (patientsList) {
      if (hasInsurance(currentPatient)) {
        setInsuranceDetails(currentPatient?.insurance);
      } else {
        setInsuranceDetails(null);
      }
    }
  }, [currentPatient, patientsList, getAccountHolder]);
  useEffect(() => {
    fetchCashPrice();
  }, [fetchCashPrice]);
  useEffect(() => {
    if (userDetails.id) {
      trackPageView("dermatology-visit-pricing", userDetails.id);
      trackDropOffRate("dermatology-visit-pricing", userDetails.id);
    }
  }, [userDetails]);
  useEffect(() => {
    getPatients();
  }, [getPatients]);

  async function AcceptPricing() {
    if (selected === "insurance") {
      const keysToCheck = [
        "insurance_company",
        "member_name",
        "relationship_to_patient",
        "member_id",
        "member_dob",
        "insurance_back_cover_photo",
        "insurance_front_cover_photo",
      ];

      for (let key of keysToCheck) {
        if (
          excludeImageContainer.includes(
            insurance_details?.insurance_company
          ) &&
          (key === "insurance_front_cover_photo" ||
            key === "insurance_back_cover_photo")
        ) {
          continue;
        }

        if (
          insurance_details[key] === null ||
          insurance_details[key] === undefined ||
          insurance_details[key] === ""
        ) {
          showAlert("Please provide the missing insurance details", "error");
          return;
        }
      }

      if (insurance_details?.has_secondary_insurance) {
        for (let key of keysToCheck) {
          if (
            excludeImageContainer.includes(
              insurance_details?.secondary_insurance?.insurance_company
            ) &&
            (key === "insurance_front_cover_photo" ||
              key === "insurance_back_cover_photo")
          ) {
            continue;
          }

          if (
            insurance_details?.secondary_insurance[key] === null ||
            insurance_details?.secondary_insurance[key] === undefined ||
            insurance_details?.secondary_insurance[key] === ""
          ) {
            showAlert(
              "Please provide the missing secondary insurance details",
              "error"
            );
            return;
          }
        }
      }
    }

    trackEvent("book_appointment_derma_visit", {
      uid: userDetails.id,
      paymentMethod: selected,
    });
    updateVisit({
      payload: {
        case: {
          id: incompleteData?.id,
          payment_method: selected === "cash" ? 0 : 1,
          dermatology_steps: incompleteData?.dermatology_steps,
        },
      },
      change_current_screen: true,
      screen_to_complete: dermatology_sub_steps.select_patient,
      callBack: () => {
        dispatch(
          dermaVisitFlowReducers.setPaymentMethod({
            value: selected,
          })
        );
        setTimeout(() => {
          history.goBack();
        }, 500);
      },
    });
  }

  if (fetchingCashPrice || fetchingPatients) {
    return (
      <LoaderComponent
        showLoader={{
          state: true,
          message: "Please wait",
        }}
      />
    );
  }

  return (
    <div className="h-screen bg-veryLightBlue bg-gradient-to-b from-blue-300 to-blue-500">
      <SimpleHeader
        title="Pricing Estimate / Payment Options"
        showBackButton={!loading}
        wrapper
        bgColor="bg-white"
      />
      <div
        className={classNames(
          "px-8  overflow-y-auto pb-56 md:pb-20 md:px-0  ",
          hasInsurance(currentPatient)
            ? "h-eliminateHeaderWithFooter"
            : "h-eliminateHeaderWithPercentageViewAndFooter"
        )}
      >
        <div className="pt-8 space-y-12 wrapper">
          <MedicalAssistant
            title="Experience Hassle-Free Dermatology Care with SkyMD"
            titleClassName="text-2xl font-bold md:text-3xl text-secondaryBlue-600"
            content={
              <div className="pb-2 text-md md:pt-4 md:mr-4">
                <p>
                  We won't charge you until appointment is completed with one of
                  our{" "}
                  <strong className="text-secondaryBlue-600">
                    amazing doctors.
                  </strong>{" "}
                  We'll let you know if{" "}
                  <strong className="text-secondaryBlue-600">
                    your insurance is in-network,
                  </strong>{" "}
                  give you an{" "}
                  <strong className="text-secondaryBlue-600">
                    estimate of costs,
                  </strong>{" "}
                  and{" "}
                  <strong className="text-secondaryBlue-600">
                    verify your coverage to maximize your benefits.
                  </strong>{" "}
                  However, we recommend you contact your insurance carrier directly to confirm whether SkyMD is in-network.
                </p>
                <p>
                  If you have any questions or concerns, call our number and our
                  medical assistant team will promptly answer your queries
                </p>
              </div>
            }
          />
          <ChoosePaymentMethod
            acceptInsurance={incompleteData?.accept_insurance}
            speciality={speciality.dermatology}
            currentPatient={getCurrentPatient(
              incompleteData?.patient_id,
              patientsList
            )}
            isInsuranceExpired={false}
            cashPriceResponse={cashPriceResponse}
            insuranceDetails={{
              ...insurance_details,
            }}
            onClickAddInsurance={() => {
              setShowModal({
                state: true,
                children: (_, onClose) => (
                  <InsuranceUpdateModal
                    onClose={onClose}
                    insuranceDetails={insurance_details}
                    currentPatient={getCurrentPatient(
                      incompleteData?.patient_id,
                      patientsList
                    )}
                    currentSpeciality={speciality.dermatology}
                  />
                ),
                data: null,
              });
              trackEvent("add_insurance_pricing_est");
            }}
            onClickUpdateInsurance={(
              insurance_details,
              currentPatient,
              insuranceType,
              isSecondary
            ) => {
              setShowModal({
                state: true,
                children: (_, onClose) => (
                  <InsuranceUpdateModal
                    onClose={onClose}
                    insuranceDetails={insurance_details}
                    currentPatient={currentPatient}
                    currentSpeciality={speciality.dermatology}
                    insuranceType={insuranceType || 1}
                    isSecondary={isSecondary || false}
                  />
                ),
                data: null,
              });
              trackEvent("update_insurance_pricing_est");
            }}
            onSelect={setSelected}
            selected={selected}
          />
        </div>
        <HelpButton bottomClass={"bottom-40"} />
      </div>
      <BottomFixedButton
        label="Continue"
        bgColor="bg-white"
        onClick={!selected ? null : AcceptPricing}
        loading={loading}
        disabled={
          loading ||
          !selected ||
          (selected === "insurance"
            ? insurance_details?.auth_id_required &&
              !incompleteData?.insurance_authorisation?.auth_id
            : false)
        }
        secondButton={
          <Button
            label={"No insurance, Skip and provide later"}
            outlined
            onClick={AcceptPricing}
            loading={loading}
            className={hasInsurance(currentPatient) && "hidden"}
          />
        }
      />
    </div>
  );
}

export default DermatologyVisitPricing;
